import React, { Component, createRef, Dispatch } from 'react';
import { connect } from 'react-redux';
import { Grid } from '@material-ui/core';
import {
    SofaInput,
    PasswordStrength,
    AbstractSofaForm,
    maxLengthFilter,
    SofaForm,
    SofaFormControl,
    SofaFormControlDescription,
    SofaFormValidator,
    stringRequiredValidator,
    MAX_EMAIL_LENGTH,
    MAX_FIELD_LENGTH,
    EMAIL_REGEXP,
    regexpValidator,
    textFilter,
    getPropsForSofaInput,
    passwordValidator,
    PASSWORD_MIN_LENGTH_REQUIRED,
    PASSWORD_NUMBER_OR_SPECIAL_REQUIRED,
    PASSWORD_UPPERCASE_REQUIRED,
    PASSWORD_NO_SPACES_REQUIRED,
    PASSWORD_NUMBER_OR_SPECIAL_REGEXP,
    PASSWORD_UPPERCASE_REGEXP,
    SofaFormFilter
} from '@sofology/sofologycomponents';

import './RegisterForm.scss';

import { SofaFormComponent } from 'interfaces';
import {
    CONFIRM_EMAIL_ERROR_MESSAGE,
    EMAIL_NOT_MATCH_ERROR_MESSAGE,
    EMAIL_REQUIRED_ERROR_MESSAGE,
    EMAIL_NOT_VALID_ERROR_MESSAGE,
    ACCOUNT_EXISTS_WARNING
} from '../DeliveryDetailsForm/deliveryDetailsAdditionalData';
import { AppState } from 'store';
import { errorToBasketErrorModel, generateDispatchToPropsMap, NOT_ALLOW_ANY_SPACES_REGEX } from 'helpers';
import { RegisterFormData } from '../CheckoutAuthentication/CheckoutAuthentication';
import { checkEmail, CheckEmailFunc } from 'store/checkout/actions';
import { getControlFilters, getStringControlValidators, DataAccessObjectType, SofologyComponentsToIlpQuestionaryNames } from 'helpers/dynamicFormHelpers';
import { IlpQuestionDescriptions, StringPropertyDescription } from 'models';
import { makeValidatorsOptional } from 'helpers/dynamicFormHelpers/makeInputValidatorsOptional';


interface OwnProps {
}

interface OwnPropsWithDefault {
    ref?: React.RefObject<RegisterFormType>;
    formData: RegisterFormData;
    goToLoginPage: () => void;
    hasAuthErrors: boolean;
}

interface OwnState {
    registerForm: SofaForm<RegisterFormControls>;
}

interface StateProps {
    userExists: boolean | null;
    ifcQuestionSet: IlpQuestionDescriptions | null;
}

interface DispatchProps {
    checkEmail: CheckEmailFunc;
}

interface RegisterFormControls extends AbstractSofaForm {
    firstName: SofaFormControl<string>;
    lastName: SofaFormControl<string>;
    email: SofaFormControl<string>;
    confirmEmail: SofaFormControl<string>;
    password: SofaFormControl<string>;
    confirmPassword: SofaFormControl<string>;
}

class RegisterForm extends Component<OwnProps & OwnPropsWithDefault & StateProps & DispatchProps, OwnState> implements SofaFormComponent<RegisterFormData> {
    static defaultProps: Partial<OwnPropsWithDefault> = {
        formData: {
            firstName: '',
            lastName: '',
            email: '',
            confirmEmail: '',
            password: '',
            confirmPassword: ''
        }
    };

    constructor(props: OwnProps & OwnPropsWithDefault & StateProps & DispatchProps) {
        super(props);

        this.state = {
            registerForm: new SofaForm<RegisterFormControls>(
                this.createFormControlDescriptions(props),
                updateStateFunc => {
                    this.setState(updateStateFunc);
                },
                'registerForm',
                () => this.state,
                errorToBasketErrorModel
            )
        };
    }

    componentDidMount() {
        this.state.registerForm.controls.firstName.setValue(this.props.formData.firstName);
        this.state.registerForm.controls.lastName.setValue(this.props.formData.lastName);
        this.state.registerForm.controls.email.setValue(this.props.formData.email);
        this.state.registerForm.controls.confirmEmail.setValue(this.props.formData.confirmEmail);
        this.state.registerForm.controls.password.setValue(this.props.formData.password);
        this.state.registerForm.controls.confirmPassword.setValue(this.props.formData.confirmPassword);
    }

    componentDidUpdate(prevProps: Readonly<OwnProps & StateProps & DispatchProps>, prevState: Readonly<OwnState>): void {
        if (prevProps.userExists === null && this.props.userExists === true) {
            this.validate();
        }

        if (prevState.registerForm.controls.email.value !== this.state.registerForm.controls.email.value
            && this.state.registerForm.controls.confirmEmail.isBlurHandled) {
            this.state.registerForm.controls.confirmEmail.validate();
        }

        if (prevState.registerForm.controls.password.value !== this.state.registerForm.controls.password.value
            && this.state.registerForm.controls.confirmPassword.isBlurHandled) {
            this.state.registerForm.controls.confirmPassword.validate();
        }
    }

    createFormControlDescriptions(
        props: OwnProps & StateProps & OwnPropsWithDefault & DispatchProps
    ): Record<keyof RegisterFormControls, SofaFormControlDescription<any>> {
        const defaultControlsDescriptions = {
            firstName: {
                name: 'firstName',
                defaultValue: this.props.formData.firstName,
                ref: createRef<SofaInput>()
            } as SofaFormControlDescription<string>,
            lastName: {
                name: 'lastName',
                defaultValue: this.props.formData.lastName,
                ref: createRef<SofaInput>()
            } as SofaFormControlDescription<string>,
            email: {
                name: 'email',
                defaultValue: this.props.formData.email,
                filters: [
                    maxLengthFilter(MAX_EMAIL_LENGTH),
                    textFilter(NOT_ALLOW_ANY_SPACES_REGEX)
                ],
                validators: [
                    stringRequiredValidator(EMAIL_REQUIRED_ERROR_MESSAGE),
                    regexpValidator(EMAIL_NOT_VALID_ERROR_MESSAGE, EMAIL_REGEXP),
                    this.existingEmailServerValidator()
                ],
                ref: createRef<SofaInput>()
            } as SofaFormControlDescription<string>,
            confirmEmail: {
                name: 'confirmEmail',
                defaultValue: this.props.formData.confirmEmail,
                filters: [
                    maxLengthFilter(MAX_EMAIL_LENGTH),
                    textFilter(NOT_ALLOW_ANY_SPACES_REGEX)
                ],
                validators: [
                    stringRequiredValidator(CONFIRM_EMAIL_ERROR_MESSAGE),
                    this.compareEmailsValidator(EMAIL_NOT_MATCH_ERROR_MESSAGE)
                ],
                ref: createRef<SofaInput>()
            } as SofaFormControlDescription<string>,
            password: {
                name: 'password',
                defaultValue: this.props.formData.password,
                filters: [
                    maxLengthFilter(MAX_FIELD_LENGTH)
                ],
                validators: [
                    passwordValidator(
                        {
                            minLength: PASSWORD_MIN_LENGTH_REQUIRED,
                            numberOrSpecial: PASSWORD_NUMBER_OR_SPECIAL_REQUIRED,
                            uppercase: PASSWORD_UPPERCASE_REQUIRED,
                            noSpaces: PASSWORD_NO_SPACES_REQUIRED
                        },
                        {
                            numberOrSpecial: PASSWORD_NUMBER_OR_SPECIAL_REGEXP,
                            uppercase: PASSWORD_UPPERCASE_REGEXP
                        }
                    )
                ],
                ref: createRef<SofaInput>()
            } as SofaFormControlDescription<string>,
            confirmPassword: {
                name: 'confirmPassword',
                defaultValue: this.props.formData.confirmPassword,
                filters: [maxLengthFilter(MAX_FIELD_LENGTH)],
                validators: [
                    stringRequiredValidator('Please confirm your password'),
                    this.comparePasswordsValidator('Passwords must match')
                ],
                ref: createRef<SofaInput>()
            } as SofaFormControlDescription<string>
        };

        const dataAccessObject: DataAccessObjectType = {};

        const questionSet = this.props.ifcQuestionSet!;

        dataAccessObject[SofologyComponentsToIlpQuestionaryNames.CustomerFirstName] = () =>
            this.state.registerForm.controls.firstName.value;
        const firstNameQuestionProperty = {
            ...questionSet.customer.properties.forename,
            required: false
        } as StringPropertyDescription;
        defaultControlsDescriptions.firstName.filters =
            getControlFilters(firstNameQuestionProperty) as SofaFormFilter<string>[];
        defaultControlsDescriptions.firstName.validators =
            makeValidatorsOptional(getStringControlValidators(firstNameQuestionProperty, dataAccessObject));


        dataAccessObject[SofologyComponentsToIlpQuestionaryNames.CustomerSurname] = () =>
            this.state.registerForm.controls.lastName.value;
        const surnameQuestionProperty = {
            ...questionSet.customer.properties.surname,
            label: 'Last name',
            required: false
        } as StringPropertyDescription;
        defaultControlsDescriptions.lastName.filters =
            getControlFilters(surnameQuestionProperty) as SofaFormFilter<string>[];
        defaultControlsDescriptions.lastName.validators =
            makeValidatorsOptional(getStringControlValidators(surnameQuestionProperty, dataAccessObject));

        return defaultControlsDescriptions;
    }

    handleGoToLoginPageClick = () => {
        this.props.goToLoginPage();
    };

    handlePasteEvent = (e: React.ClipboardEvent) => {
        e.preventDefault();

        return false;
    };

    getInputProps = (control: SofaFormControl<any>) => ({
        ...getPropsForSofaInput(control),
        addErrorClass: this.props.hasAuthErrors || Boolean(control.errorMessage),
        helperText: this.props.hasAuthErrors ? null : control.errorMessage
    });

    render(): JSX.Element {
        return (
            <div className='register-form'>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SofaInput
                            label='First name'
                            shape='pill'
                            inputProps={{ autoCorrect: 'off', autoComplete: 'given-name' }}
                            {...this.getInputProps(this.state.registerForm.controls.firstName)}
                            helperTextType={this.state.registerForm.controls.firstName.errorMessage ? 'error' : 'info'}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SofaInput
                            label='Last name'
                            shape='pill'
                            inputProps={{ autoCorrect: 'off', autoComplete: 'family-name' }}
                            {...this.getInputProps(this.state.registerForm.controls.lastName)}
                            helperTextType={this.state.registerForm.controls.lastName.errorMessage ? 'error' : 'info'}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SofaInput
                            label='Email address*'
                            shape='pill'
                            inputProps={{ inputMode: 'email', autoCapitalize: 'off', autoCorrect: 'off', autoComplete: 'email' }}
                            {...this.getInputProps(this.state.registerForm.controls.email)}
                            helperTextType={this.state.registerForm.controls.email.errorMessage ? 'error' : 'info'}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SofaInput
                            label='Confirm email address*'
                            shape='pill'
                            inputProps={{ onPaste: this.handlePasteEvent, inputMode: 'email', autoCapitalize: 'off', autoCorrect: 'off', autoComplete: 'off' }}
                            {...this.getInputProps(this.state.registerForm.controls.confirmEmail)}
                            helperTextType={this.state.registerForm.controls.confirmEmail.errorMessage ? 'error' : 'info'}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SofaInput
                            label='Password*'
                            type='password'
                            shape='pill'
                            inputProps={{ autoCapitalize: 'off', autoCorrect: 'off', autoComplete: 'new-password' }}
                            {...this.getInputProps(this.state.registerForm.controls.password)}
                            helperText={null}
                        >
                            <PasswordStrength
                                value={this.state.registerForm.controls.password.value}
                                showErrors={Boolean(this.state.registerForm.controls.password.errorMessage)}
                            />
                        </SofaInput>
                    </Grid>
                    <Grid item xs={12}>
                        <SofaInput
                            label='Confirm password*'
                            type='password'
                            shape='pill'
                            inputProps={{
                                autoCapitalize: 'off',
                                autoCorrect: 'off',
                                autoComplete: 'new-password'
                            }}
                            {...this.getInputProps(this.state.registerForm.controls.confirmPassword)}
                            helperTextType={this.state.registerForm.controls.confirmPassword.errorMessage ? 'error' : 'info'}
                        />
                    </Grid>
                </Grid>
            </div>
        );
    }

    renderAccountExistsError(shouldShowError: boolean): JSX.Element | null {
        return (
            shouldShowError
                ? <span>
                    {ACCOUNT_EXISTS_WARNING.prefix}
                    <a className='register-form__create-account-link' onClick={this.handleGoToLoginPageClick}>{ACCOUNT_EXISTS_WARNING.linkText}</a>
                    {ACCOUNT_EXISTS_WARNING.postfix}
                </span>
                : null
        );
    }

    checkExistingEmail = () => {
        const newEmailInput = this.state.registerForm.controls.email.value;
        this.props.checkEmail(newEmailInput.toLowerCase());
    };

    compareEmailsValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string) => {
            const email = this.state?.registerForm.controls.email.value;
            const shouldShowError = Boolean(email !== value);

            return Promise.resolve(shouldShowError ? errorMessage : null);
        };
    };

    comparePasswordsValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string) => {
            const password = this.state?.registerForm.controls.password.value;
            const shouldShowError = Boolean(password !== value);

            return Promise.resolve(shouldShowError ? errorMessage : null);
        };
    };

    getFormData(): RegisterFormData {
        return {
            firstName: this.state?.registerForm.controls.firstName.value,
            lastName: this.state?.registerForm.controls.lastName.value,
            email: this.state?.registerForm.controls.email.value,
            confirmEmail: this.state?.registerForm.controls.confirmEmail.value,
            password: this.state?.registerForm.controls.password.value,
            confirmPassword: this.state?.registerForm.controls.confirmPassword.value
        };
    }

    public validate = (): Promise<SofaFormControl<any>[]> => {
        return this.state.registerForm.validate();
    };

    existingEmailServerValidator = (): SofaFormValidator<string> => {
        return (value: string, isChangedAfterExternalValidation: boolean) => {
            const shouldShowError = Boolean(
                !isChangedAfterExternalValidation
                && this.props.userExists
                && value === this.state.registerForm.controls.email.value
            );

            return Promise.resolve(this.renderAccountExistsError(shouldShowError));
        };
    };
}

function mapStateToProps(state: AppState): StateProps {
    return {
        userExists: state.checkoutState.userExists,
        ifcQuestionSet: state.checkoutState.ifcQuestionSet
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [checkEmail]);
}

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(RegisterForm);
export type RegisterFormType = RegisterForm;
